import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, Divider, CircularProgress } from '@mui/material';
import { reviewerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// The declaration is authored by an admin, so this is not a defence against a
// hostile author — it is a guard against a stray <script> or an onclick pasted
// in from a word processor. The real access control is server-side: assignments
// for a form with an outstanding declaration are not returned at all.
function sanitize(html) {
  if (!html) return '';
  return String(html)
    .replace(/<\s*(script|iframe|object|embed|link|meta)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|iframe|object|embed|link|meta)[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript\s*:/gi, '');
}

export default function DeclarationGate(props) {
  var ps = useState(null); var pending = ps[0]; var setPending = ps[1];
  var bs = useState(false); var busy = bs[0]; var setBusy = bs[1];
  var navigate = useNavigate();
  var auth = useAuth();

  function load() {
    reviewerAPI.pendingDeclarations()
      .then(function(r) { setPending(r.data.data || []); })
      .catch(function() { setPending([]); });
  }

  useEffect(load, []);

  function accept() {
    var current = pending[0];
    setBusy(true);
    reviewerAPI.respondToDeclaration(current.form_id, {action: 'accepted'})
      .then(function() {
        var rest = pending.slice(1);
        setPending(rest);
        setBusy(false);
        // Last one cleared: reload so the queue appears.
        if (rest.length === 0 && props.onAccepted) props.onAccepted();
      })
      .catch(function() { setBusy(false); });
  }

  function decline() {
    var current = pending[0];
    setBusy(true);
    reviewerAPI.respondToDeclaration(current.form_id, {action: 'declined'})
      .then(function() {
        auth.logout();
        navigate('/login', {replace: true});
      })
      .catch(function() {
        auth.logout();
        navigate('/login', {replace: true});
      });
  }

  var E = React.createElement;
  if (!pending || pending.length === 0) return null;

  var current = pending[0];

  return E(Dialog, {open: true, maxWidth: 'md', fullWidth: true,
    disableEscapeKeyDown: true, onClose: function(){ /* must be answered */ }},
    E(DialogTitle, null,
      current.title || 'Declaration',
      E(Typography, {variant:'body2', color:'text.secondary'}, current.form_title)),
    E(DialogContent, {dividers:true},
      E(Box, {sx:{'& p':{mb:1.5}, '& ul, & ol':{pl:3, mb:1.5}, '& li':{mb:0.5},
        '& h1, & h2, & h3':{mt:2, mb:1}, '& a':{color:'primary.main'},
        '& table':{borderCollapse:'collapse', width:'100%'},
        '& td, & th':{border:'1px solid #ddd', p:1}},
        dangerouslySetInnerHTML:{__html: sanitize(current.html)}}),
      E(Divider, {sx:{my:2}}),
      E(Typography, {variant:'caption', color:'text.secondary'},
        'Your answer is recorded with the date, time and the exact wording shown above.'),
      pending.length > 1
        ? E(Typography, {variant:'caption', color:'text.secondary', sx:{display:'block', mt:0.5}},
            pending.length - 1 + ' more to read after this one')
        : null),
    E(DialogActions, {sx:{p:2, gap:1}},
      E(Button, {onClick:decline, disabled:busy, color:'inherit'}, 'No'),
      E(Button, {onClick:accept, disabled:busy, variant:'contained',
        startIcon: busy ? E(CircularProgress, {size:16, color:'inherit'}) : null},
        'Yes, I agree')));
}
